import { Fragment, useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  FileSearch,
  Loader2,
  RefreshCw,
  ScanSearch,
  Upload,
} from "lucide-react";
import toast from "react-hot-toast";
import AdminLayout from "../admin/AdminDashboardLayout";
import { useApiQuery } from "../../hooks/useApiQuery";
import { useApiMutation } from "../../hooks/useMutation";
import { useDashboardRole } from "../../hooks/useDashboardRole";
import { request } from "../../utils/api";
import Button from "../../utils/Button";
import Textarea from "../../utils/Textarea";

type BatchStatus = "DRAFT" | "SUBMITTED" | "VERIFIED" | "REJECTED" | string;
type RecordStatus = "PENDING" | "VERIFIED" | "REJECTED" | "INCOMPLETE" | string;
type ReviewStatus = "VERIFIED" | "REJECTED";
type BatchReviewAction = "VERIFY" | "REJECT";
type BatchSubmissionSummary = {
  batchId: number;
  action: BatchReviewAction;
  verifiedForms: number;
  rejectedForms: number;
  incompleteForms: number;
  totalForms: number;
};

type PartnerBatch = {
  id: number;
  partnerId: number;
  canvasserId: number;
  claimedForms: number;
  verifiedForms: number;
  rejectedForms: number;
  incompleteForms: number;
  status: BatchStatus;
  verificationMode: string | null;
  verifiedByUserId: number | null;
  verifiedAt: string | null;
  acknowledgedAt?: string | null;
  submittedAt: string | null;
  deliveredByUserId?: number | null;
  deliveredAt?: string | null;
  deliveredNote?: string | null;
  receiptUrl?: string | null;
  overdueFlag: boolean;
  hoursToVerify: number | null;
  accuracyScore: number | null;
  rejectionRate: number | null;
  timelinessScore: number | null;
  finalQualityScore: number | null;
  canReject?: boolean;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
};

type BatchRecord = {
  id: string;
  batchId: number;
  checkRegistrationLogId: number;
  checkRegistration: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    state: string;
  } | null;
  createdByName: string;
  signatureValid: boolean;
  completenessValid: boolean;
  formLegible: boolean;
  status: RecordStatus;
  notes: string | null;
  createdAt: string;
};

type PartnerBatchResponse = {
  success: boolean;
  message: string;
  data:
    | PartnerBatch[]
    | {
        content?: PartnerBatch[];
      };
};

type BatchRecordsResponse = {
  success: boolean;
  message: string;
  data: {
    content: BatchRecord[];
    pageNumber: number;
    pageSize: number;
    totalElements: number;
    totalPages: number;
    first: boolean;
    last: boolean;
  };
};

const formatDateValue = (value: string | null) => {
  if (!value) return "Not available";

  try {
    return format(new Date(value), "MMM dd, yyyy 'at' h:mm a");
  } catch {
    return value;
  }
};

const formatScore = (value: number | null) => {
  if (value === null || value === undefined) return "--";
  return `${Number(value).toFixed(1)}%`;
};

const getBatchStatusClasses = (status: string) => {
  switch (status?.toUpperCase()) {
    case "VERIFIED":
      return "border border-emerald-200 bg-emerald-50 text-emerald-700";
    case "SUBMITTED":
      return "border border-blue-200 bg-blue-50 text-blue-700";
    case "REJECTED":
      return "border border-rose-200 bg-rose-50 text-rose-700";
    default:
      return "border border-amber-200 bg-amber-50 text-amber-700";
  }
};

const getRecordStatusClasses = (status: string) => {
  switch (status?.toUpperCase()) {
    case "VERIFIED":
      return "border border-emerald-200 bg-emerald-50 text-emerald-700";
    case "REJECTED":
      return "border border-rose-200 bg-rose-50 text-rose-700";
    case "INCOMPLETE":
      return "border border-amber-200 bg-amber-50 text-amber-700";
    default:
      return "border border-slate-200 bg-slate-100 text-slate-700";
  }
};

const formatBooleanState = (value: boolean) => {
  return value ? "Valid" : "Invalid";
};

const getDeliveryCountdown = (verifiedAt: string | null, nowTimestamp: number) => {
  if (!verifiedAt) {
    return {
      isOverdue: false,
      label: "7 days",
    };
  }

  const verifiedAtTimestamp = new Date(verifiedAt).getTime();

  if (Number.isNaN(verifiedAtTimestamp)) {
    return {
      isOverdue: false,
      label: "7 days",
    };
  }

  const dayInMs = 24 * 60 * 60 * 1000;
  const deadline = verifiedAtTimestamp + 7 * dayInMs;
  const remaining = deadline - nowTimestamp;

  if (remaining <= 0) {
    const overdueDays = Math.max(1, Math.ceil(Math.abs(remaining) / dayInMs));

    return {
      isOverdue: true,
      label: `${overdueDays} day${overdueDays === 1 ? "" : "s"}`,
    };
  }

  const days = Math.ceil(remaining / dayInMs);

  return {
    isOverdue: false,
    label: `${days} day${days === 1 ? "" : "s"}`,
  };
};

const getCloudinaryConfig = () => ({
  cloudName:
    process.env.REACT_APP_CLOUDINARY_CLOUD_NAME ||
    process.env.REACT_APP_CLOUDINARY_CLOUDNAME ||
    process.env.REACT_APP_CLOUDINARY_NAME,
  uploadPreset:
    process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET ||
    process.env.REACT_APP_CLOUDINARY_PRESET,
});

const isAllowedReceiptFile = (file: File) => {
  return ["image/png", "image/jpg", "image/jpeg", "application/pdf"].includes(file.type);
};

const uploadReceiptToCloudinary = async (file: File) => {
  const { cloudName, uploadPreset } = getCloudinaryConfig();

  if (!cloudName || !uploadPreset) {
    throw new Error("Cloudinary cloud name or upload preset is missing from the environment.");
  }

  const formData = new FormData();
  formData.append("file", file);
  formData.append("upload_preset", uploadPreset);
  formData.append("folder", "soe-receipts");

  const response = await fetch(`https://api.cloudinary.com/v1_1/${cloudName}/auto/upload`, {
    method: "POST",
    body: formData,
  });

  if (!response.ok) {
    throw new Error("Cloudinary receipt upload failed.");
  }

  const result = await response.json();

  if (!result.secure_url) {
    throw new Error("Cloudinary did not return a receipt URL.");
  }

  return result.secure_url as string;
};

const getPersistedReviewStatus = (status: RecordStatus): ReviewStatus | undefined => {
  return status === "VERIFIED" || status === "REJECTED" ? status : undefined;
};

const getBatchList = (data: PartnerBatchResponse["data"] | undefined): PartnerBatch[] => {
  if (Array.isArray(data)) return data;
  return data?.content ?? [];
};

async function fetchAllBatchRecords(batchId: number) {
  const pageSize = 100;
  const firstPage = await request<BatchRecordsResponse>({
    method: "GET",
    url: `/paper-form-records/batch/${batchId}`,
    params: {
      page: 0,
      size: pageSize,
    },
  });

  const records = [...(firstPage.data.content ?? [])];
  const totalPages = firstPage.data.totalPages ?? 1;

  for (let currentPage = 1; currentPage < totalPages; currentPage += 1) {
    const nextPage = await request<BatchRecordsResponse>({
      method: "GET",
      url: `/paper-form-records/batch/${batchId}`,
      params: {
        page: currentPage,
        size: pageSize,
      },
    });

    records.push(...(nextPage.data.content ?? []));
  }

  return records;
}

function BatchRecordsPanel({
  batchId,
  isVerificationMode,
  reviewAction,
  selectedStatuses,
  pendingRecordId,
  onStatusChange,
  onSubmitVerification,
  isSubmittingVerification,
}: {
  batchId: number;
  isVerificationMode: boolean;
  reviewAction: BatchReviewAction | null;
  selectedStatuses: Record<string, ReviewStatus>;
  pendingRecordId: string | null;
  onStatusChange: (recordId: string, status: ReviewStatus) => void;
  onSubmitVerification: () => void;
  isSubmittingVerification: boolean;
}) {
  const [page, setPage] = useState(0);
  const pageSize = 10;

  useEffect(() => {
    setPage(0);
  }, [batchId]);

  const { data, isLoading, error } = useApiQuery<BatchRecordsResponse>({
    queryKey: ["paper-form-records", "batch", batchId, page, pageSize],
    request: {
      method: "GET",
      url: `/paper-form-records/batch/${batchId}`,
      params: {
        page,
        size: pageSize,
      },
    },
    enabled: Boolean(batchId),
  });

  const recordsPage = data?.data;
  const records = recordsPage?.content ?? [];

  if (isLoading) {
    return (
      <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-8 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin text-sky-600" />
        Loading forms in this batch...
      </div>
    );
  }

  if (error || !data?.success) {
    return (
      <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-5 text-sm text-rose-700">
        {error instanceof Error ? error.message : "Unable to load forms for this batch."}
      </div>
    );
  }

  if (records.length === 0) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white px-4 py-8 text-sm text-slate-500">
        No form records were found for this batch.
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
      <div className="border-b border-slate-200 bg-slate-50 px-4 py-3">
        <p className="text-sm font-semibold text-slate-900">Forms in batch #{batchId}</p>
        <p className="text-xs text-slate-500">Each row represents one paper-form record.</p>
        {isVerificationMode && (
          <div className="mt-3 rounded-2xl border border-teal-200 bg-teal-50 px-3 py-3 text-sm text-teal-800">
            <p className="font-semibold">
              Review mode: {reviewAction === "REJECT" ? "Reject batch" : "Verify batch"}
            </p>
            Review each paper form and choose whether to verify or reject it before submitting the batch to SOE.
          </div>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-white">
            <tr>
              {/* <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Record
              </th> */}
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Voter
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Canvasser
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Checks
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Status
              </th>
              {isVerificationMode && (
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Action
                </th>
              )}
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Created
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Notes
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-200">
            {records.map((record) => {
              const resolvedReviewStatus = selectedStatuses[record.id] ?? getPersistedReviewStatus(record.status);

              return (
              <tr
                key={record.id}
                className={`hover:bg-slate-50/80 ${isVerificationMode && resolvedReviewStatus ? getRecordStatusClasses(resolvedReviewStatus) : ""
                  }`}
              >
                {/* <td className="px-4 py-4 align-top">
                  <div className="space-y-1">
                    <p className="text-sm font-semibold text-slate-900">Log #{record.checkRegistrationLogId}</p>
                    <p className="text-xs text-slate-500 truncate">{record.id}</p>
                  </div>
                </td> */}

                <td className="px-4 py-4 align-top">
                  <div className="space-y-1 text-sm text-slate-700">
                    <p className="font-medium text-slate-900">
                      {record.checkRegistration
                        ? `${record.checkRegistration.firstName} ${record.checkRegistration.lastName}`.trim()
                        : "Not available"}
                    </p>
                    <p>{record.checkRegistration?.email || "No email"}</p>
                    <p>{record.checkRegistration?.phone || "No phone"}</p>
                    <p className="text-xs text-slate-500">{record.checkRegistration?.state || "No state"}</p>
                  </div>
                </td>

                <td className="px-4 py-4 align-top text-sm capitalize text-slate-700">
                  {record.createdByName}
                </td>

                <td className="px-4 py-4 align-top">
                  <div className="grid gap-2 text-sm text-slate-700">
                    <div className="flex items-center justify-between gap-4">
                      <span>Signature</span>
                      <span className={record.signatureValid ? "text-emerald-700 font-medium" : "text-rose-700 font-medium"}>
                        {formatBooleanState(record.signatureValid)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <span>Complete</span>
                      <span className={record.completenessValid ? "text-emerald-700 font-medium" : "text-rose-700 font-medium"}>
                        {formatBooleanState(record.completenessValid)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <span>Legible</span>
                      <span className={record.formLegible ? "text-emerald-700 font-medium" : "text-rose-700 font-medium"}>
                        {formatBooleanState(record.formLegible)}
                      </span>
                    </div>
                  </div>
                </td>

                <td className="px-4 py-4 align-top">
                  <span
                    className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${getRecordStatusClasses(
                      isVerificationMode ? resolvedReviewStatus ?? record.status : record.status
                    )}`}
                  >
                    {(isVerificationMode ? resolvedReviewStatus ?? record.status : record.status).replace(/_/g, " ")}
                  </span>
                </td>

                {isVerificationMode && (
                  <td className="px-4 py-4 align-top">
                    <select
                      value={resolvedReviewStatus ?? ""}
                      onChange={(event) => onStatusChange(record.id, event.target.value as ReviewStatus)}
                      disabled={pendingRecordId === record.id}
                      className={`w-full rounded-xl border px-3 py-2 text-sm font-medium outline-none transition disabled:cursor-not-allowed disabled:opacity-60 ${getRecordStatusClasses(
                        resolvedReviewStatus ?? "PENDING"
                      )}`}
                    >
                      <option value="" disabled>
                        {pendingRecordId === record.id ? "Saving action..." : "Select action"}
                      </option>
                      <option value="VERIFIED">Verify form</option>
                      <option value="REJECTED">Reject form</option>
                    </select>
                  </td>
                )}

                <td className="px-4 py-4 align-top text-sm text-slate-700">
                  {formatDateValue(record.createdAt)}
                </td>

                <td className="px-4 py-4 align-top text-sm text-slate-500">
                  {record.notes?.trim() || "No notes"}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {recordsPage && recordsPage.totalPages > 1 && (
        <div className="flex flex-col gap-3 border-t border-slate-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-slate-500">
            Showing {recordsPage.pageNumber * recordsPage.pageSize + 1} to{" "}
            {Math.min((recordsPage.pageNumber + 1) * recordsPage.pageSize, recordsPage.totalElements)} of{" "}
            {recordsPage.totalElements} forms
          </p>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((current) => Math.max(0, current - 1))}
              disabled={recordsPage.first}
              className="rounded-full border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 transition hover:border-teal-300 hover:text-teal-700 disabled:cursor-not-allowed disabled:border-slate-200 disabled:text-slate-400"
            >
              Previous
            </button>
            <span className="text-sm text-slate-500">
              Page {recordsPage.pageNumber + 1} of {recordsPage.totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((current) => current + 1)}
              disabled={recordsPage.last}
              className="rounded-full border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 transition hover:border-teal-300 hover:text-teal-700 disabled:cursor-not-allowed disabled:border-slate-200 disabled:text-slate-400"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {isVerificationMode && (
        <div className="flex justify-end border-t border-slate-200 bg-slate-50 px-4 py-4">
          <button
            type="button"
            onClick={onSubmitVerification}
            disabled={isSubmittingVerification}
            className="inline-flex items-center gap-2 rounded-full border border-teal-200 bg-teal-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-teal-700 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-300"
          >
            {isSubmittingVerification ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {reviewAction === "REJECT" ? "Closing batch..." : "Preparing SOE submission..."}
              </>
            ) : (
              reviewAction === "REJECT" ? "Reject and close batch" : "Verify batch and submit to SOE"
            )}
          </button>
        </div>
      )}
    </div>
  );
}

const PartnerBatchRequests = () => {
  const { dashboardRole, isSupervisor } = useDashboardRole();
  const [expandedBatchId, setExpandedBatchId] = useState<number | null>(null);
  const [reviewingBatchId, setReviewingBatchId] = useState<number | null>(null);
  const [reviewAction, setReviewAction] = useState<BatchReviewAction | null>(null);
  const [submittingBatchId, setSubmittingBatchId] = useState<number | null>(null);
  const [verifyingRecordId, setVerifyingRecordId] = useState<string | null>(null);
  const [acknowledgingBatchId, setAcknowledgingBatchId] = useState<number | null>(null);
  const [acknowledgedBatchIds, setAcknowledgedBatchIds] = useState<Set<number>>(new Set());
  const [acknowledgementModalBatch, setAcknowledgementModalBatch] = useState<PartnerBatch | null>(null);
  const [acknowledgementNotes, setAcknowledgementNotes] = useState("");
  const [pendingBatchSubmission, setPendingBatchSubmission] = useState<BatchSubmissionSummary | null>(null);
  const [batchFormStatuses, setBatchFormStatuses] = useState<Record<number, Record<string, ReviewStatus>>>({});
  const [nowTimestamp, setNowTimestamp] = useState(Date.now());
  const [deliveryNotes, setDeliveryNotes] = useState<Record<number, string>>({});
  const [receiptUrls, setReceiptUrls] = useState<Record<number, string>>({});
  const [receiptFileNames, setReceiptFileNames] = useState<Record<number, string>>({});
  const [uploadingReceiptBatchId, setUploadingReceiptBatchId] = useState<number | null>(null);
  const [deliveringBatchId, setDeliveringBatchId] = useState<number | null>(null);

  useEffect(() => {
    const timer = window.setInterval(() => setNowTimestamp(Date.now()), 60 * 1000);
    return () => window.clearInterval(timer);
  }, []);

  const {
    data: response,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useApiQuery<PartnerBatchResponse>({
    queryKey: ["paper-form-batches"],
    request: {
      method: "GET",
      url: "/paper-form-batches",
    },
  });

  const verifyBatchMutation = useApiMutation<{
    success: boolean;
    message?: string;
  }>({
    onSuccess: async (result) => {
      toast.success(
        result?.message || `Batch ${reviewAction === "REJECT" ? "rejected" : "verified"} successfully`
      );
      setReviewingBatchId(null);
      setReviewAction(null);
      await refetch();
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to verify batch";
      toast.error(errorMessage);
    },
  });

  const acknowledgeBatchMutation = useApiMutation<{
    success: boolean;
    message?: string;
  }>({
    onSuccess: async (result) => {
      if (acknowledgingBatchId) {
        setAcknowledgedBatchIds((current) => new Set(current).add(acknowledgingBatchId));
      }

      toast.success(result?.message || "Batch collection acknowledged successfully", {
        id: "batch-acknowledgement-toast",
      });
      setAcknowledgementModalBatch(null);
      setAcknowledgementNotes("");
      await refetch();
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to acknowledge batch collection";
      toast.error(errorMessage, { id: "batch-acknowledgement-toast" });
    },
  });

  const verifyFormMutation = useApiMutation<{
    success: boolean;
    message?: string;
  }>({});

  const deliverBatchMutation = useApiMutation<{
    success: boolean;
    message?: string;
  }>({
    onSuccess: async (result) => {
      toast.success(result?.message || "Supervisor of Elections receipt submitted successfully");
      setDeliveryNotes((current) => {
        const next = { ...current };
        if (deliveringBatchId) delete next[deliveringBatchId];
        return next;
      });
      setReceiptUrls((current) => {
        const next = { ...current };
        if (deliveringBatchId) delete next[deliveringBatchId];
        return next;
      });
      setReceiptFileNames((current) => {
        const next = { ...current };
        if (deliveringBatchId) delete next[deliveringBatchId];
        return next;
      });
      await refetch();
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to submit Supervisor of Elections receipt";
      toast.error(errorMessage);
    },
  });

  const batches = useMemo(() => getBatchList(response?.data), [response?.data]);

  // Check if any batch has quality scores to show the Quality column
  const hasQualityScores = useMemo(() => {
    return batches?.some(
      batch =>
        (batch.accuracyScore !== null && batch.accuracyScore !== undefined) ||
        (batch.rejectionRate !== null && batch.rejectionRate !== undefined) ||
        (batch.timelinessScore !== null && batch.timelinessScore !== undefined) ||
        (batch.finalQualityScore !== null && batch.finalQualityScore !== undefined)
    );
  }, [batches]);

  const summary = useMemo(() => {
    return batches.reduce(
      (acc, batch) => {
        acc.totalBatches += 1;
        acc.claimedForms += batch.claimedForms || 0;
        acc.verifiedForms += batch.verifiedForms || 0;

        if (batch.status?.toUpperCase() === "SUBMITTED") acc.submittedBatches += 1;
        if (batch.overdueFlag) acc.overdueBatches += 1;

        return acc;
      },
      {
        totalBatches: 0,
        claimedForms: 0,
        verifiedForms: 0,
        submittedBatches: 0,
        overdueBatches: 0,
      }
    );
  }, [batches]);

  const toggleExpandedRow = (batchId: number) => {
    setExpandedBatchId((current) => {
      const nextBatchId = current === batchId ? null : batchId;

      if (nextBatchId !== batchId && reviewingBatchId === batchId) {
        setReviewingBatchId(null);
        setReviewAction(null);
      }

      return nextBatchId;
    });
  };

  const handleStartBatchReview = (batch: PartnerBatch, action: BatchReviewAction) => {
    if (action === "REJECT" && batch.canReject !== true) {
      toast.error("This batch can only be rejected when every form has invalid signature, completeness, and legibility checks.");
      return;
    }

    setExpandedBatchId(batch.id);
    setReviewingBatchId(batch.id);
    setReviewAction(action);
  };

  const handleOpenAcknowledgementModal = (batch: PartnerBatch) => {
    setAcknowledgementModalBatch(batch);
    setAcknowledgementNotes("");
  };

  const handleAcknowledgeBatchCollection = async () => {
    if (!acknowledgementModalBatch) return;

    const batchId = acknowledgementModalBatch.id;
    setAcknowledgingBatchId(batchId);

    try {
      toast.loading("Acknowledging batch collection...", {
        id: "batch-acknowledgement-toast",
      });

      await acknowledgeBatchMutation.mutateAsync({
        method: "POST",
        url: `/paper-form-batches/${batchId}/acknowledge`,
        data: {
          notes: acknowledgementNotes.trim() || null,
        },
      });
    } finally {
      setAcknowledgingBatchId(null);
    }
  };

  const handleBatchRecordStatusChange = (batchId: number, recordId: string, status: ReviewStatus) => {
    const previousStatus = batchFormStatuses[batchId]?.[recordId];

    setBatchFormStatuses((current) => ({
      ...current,
      [batchId]: {
        ...(current[batchId] ?? {}),
        [recordId]: status,
      },
    }));
    setVerifyingRecordId(recordId);

    verifyFormMutation.mutate(
      {
        method: "POST",
        url: `/paper-form-records/${recordId}/verify`,
        data: {
          verified: status === "VERIFIED",
        },
      },
      {
        onSuccess: (result) => {
          toast.success(result?.message || `Form ${status === "VERIFIED" ? "verified" : "rejected"} successfully`);
        },
        onError: (error: any) => {
          setBatchFormStatuses((current) => {
            const nextBatchStatuses = { ...(current[batchId] ?? {}) };

            if (previousStatus) {
              nextBatchStatuses[recordId] = previousStatus;
            } else {
              delete nextBatchStatuses[recordId];
            }

            return {
              ...current,
              [batchId]: nextBatchStatuses,
            };
          });

          const errorMessage =
            error?.response?.data?.message ||
            error?.message ||
            "Failed to update form review action";
          toast.error(errorMessage);
        },
        onSettled: () => {
          setVerifyingRecordId(null);
        },
      }
    );
  };

  const handleSubmitBatchVerification = async (batchId: number) => {
    setSubmittingBatchId(batchId);

    try {
      const currentSelections = batchFormStatuses[batchId] ?? {};
      const resolvedReviewAction = reviewAction ?? "VERIFY";
      const batch = batches.find((item) => item.id === batchId);

      if (resolvedReviewAction === "REJECT" && batch?.canReject !== true) {
        toast.error("This batch can only be rejected when every form has invalid signature, completeness, and legibility checks.");
        return;
      }

      const allBatchRecords = await fetchAllBatchRecords(batchId);

      const totals = allBatchRecords.reduce(
        (acc, record) => {
          const resolvedStatus = currentSelections[record.id] ?? getPersistedReviewStatus(record.status);

          if (resolvedStatus === "REJECTED") acc.rejectedForms += 1;
          else if (resolvedStatus === "VERIFIED") acc.verifiedForms += 1;
          else acc.incompleteForms += 1;

          return acc;
        },
        {
          verifiedForms: 0,
          rejectedForms: 0,
          incompleteForms: 0,
          totalForms: allBatchRecords.length,
        }
      );

      setPendingBatchSubmission({
        batchId,
        action: resolvedReviewAction,
        ...totals,
      });
    } finally {
      setSubmittingBatchId(null);
    }
  };

  const handleProceedBatchSubmissionToSoe = async () => {
    if (!pendingBatchSubmission) return;

    setSubmittingBatchId(pendingBatchSubmission.batchId);

    try {
      await verifyBatchMutation.mutateAsync({
        method: "POST",
        url: `/paper-form-batches/${pendingBatchSubmission.batchId}/${pendingBatchSubmission.action === "REJECT" ? "reject" : "verify"}`,
        data: {
          verifiedForms: pendingBatchSubmission.verifiedForms,
          rejectedForms: pendingBatchSubmission.rejectedForms,
          incompleteForms: pendingBatchSubmission.incompleteForms,
          verificationMode: "FULL",
        },
      });

      setBatchFormStatuses((current) => {
        const next = { ...current };
        delete next[pendingBatchSubmission.batchId];
        return next;
      });
      setPendingBatchSubmission(null);
    } finally {
      setSubmittingBatchId(null);
    }
  };

  const handleReceiptUpload = async (batchId: number, file: File | undefined) => {
    if (!file) return;

    if (!isAllowedReceiptFile(file)) {
      toast.error("Upload a PNG, JPG, JPEG, or PDF receipt.");
      return;
    }

    setUploadingReceiptBatchId(batchId);

    try {
      const receiptUrl = await uploadReceiptToCloudinary(file);
      setReceiptUrls((current) => ({ ...current, [batchId]: receiptUrl }));
      setReceiptFileNames((current) => ({ ...current, [batchId]: file.name }));
      toast.success("Supervisor of Elections receipt uploaded successfully");
    } catch (error: any) {
      toast.error(error?.message || "Failed to upload receipt");
    } finally {
      setUploadingReceiptBatchId(null);
    }
  };

  const handleSubmitDeliveryReceipt = async (batchId: number) => {
    const receiptUrl = receiptUrls[batchId];

    if (!receiptUrl) {
      toast.error("Upload the Supervisor of Elections receipt before submitting.");
      return;
    }

    setDeliveringBatchId(batchId);

    try {
      await deliverBatchMutation.mutateAsync({
        method: "POST",
        url: `/paper-form-batches/${batchId}/deliver`,
        data: {
          note: deliveryNotes[batchId]?.trim() || null,
          receiptUrl,
        },
      });
    } finally {
      setDeliveringBatchId(null);
    }
  };

  return (
    <AdminLayout role={dashboardRole}>
      <div className="min-h-screen rounded-2xl bg-slate-50 sm:bg-transparent">
        <div className="mx-auto w-full space-y-6">
          <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_18px_50px_-28px_rgba(15,23,42,0.35)]">
            <div className="border-b border-slate-200 bg-[radial-gradient(circle_at_top_left,_rgba(20,184,166,0.18),_transparent_35%),linear-gradient(135deg,_#f8fafc_0%,_#f1fdfb_50%,_#ffffff_100%)] px-6 py-7 sm:px-8">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="max-w-3xl">
                  <p className="text-xs font-semibold uppercase tracking-[0.32em] text-teal-700">
                    {isSupervisor ? "Supervisor Oversight" : "Partner Oversight"}
                  </p>
                  <h1 className="mt-2 text-3xl font-semibold tracking-tight text-slate-900">
                    Batch Requests
                  </h1>
                  <p className="mt-2 text-sm leading-6 text-slate-600">
                    Track every paper-form batch submitted under your partner account,
                    then expand any row to inspect the individual forms attached to that batch.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => refetch()}
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-teal-300 hover:text-teal-700"
                >
                  <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
                  Refresh
                </button>
              </div>
            </div>

            <div className="grid gap-4 p-6 sm:grid-cols-2 xl:grid-cols-3 sm:p-8">
              <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4 outline outline-1 outline-slate-100">
                <div className="flex items-center gap-3">
                  <div className="rounded-2xl bg-teal-100 p-3 text-teal-700">
                    <ClipboardList className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Total batches</p>
                    <p className="text-2xl font-semibold text-slate-900">{summary.totalBatches}</p>
                  </div>
                </div>
              </div>

              {/* <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4 outline outline-1 outline-slate-100">
                <div className="flex items-center gap-3">
                  <div className="rounded-2xl bg-blue-100 p-3 text-blue-700">
                    <ScanSearch className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Submitted batches</p>
                    <p className="text-2xl font-semibold text-slate-900">{summary.submittedBatches}</p>
                  </div>
                </div>
              </div> */}

              <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4 outline outline-1 outline-slate-100">
                <div className="flex items-center gap-3">
                  <div className="rounded-2xl bg-emerald-100 p-3 text-emerald-700">
                    <CheckCircle2 className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Verified forms</p>
                    <p className="text-2xl font-semibold text-slate-900">{summary.verifiedForms}</p>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4 outline outline-1 outline-slate-100">
                <div className="flex items-center gap-3">
                  <div className="rounded-2xl bg-amber-100 p-3 text-amber-700">
                    <FileSearch className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Overdue batches</p>
                    <p className="text-2xl font-semibold text-slate-900">{summary.overdueBatches}</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_16px_40px_-30px_rgba(15,23,42,0.45)]">
            <div className="flex flex-col gap-2 border-b border-slate-200 px-6 py-5 sm:px-8">
              <h2 className="text-lg font-semibold text-slate-900">Partner batch register</h2>
              <p className="text-sm text-slate-500">
                Click any batch row to expand it and inspect the forms fetched for that specific batch.
              </p>
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center gap-3 px-6 py-16 text-slate-500">
                <Loader2 className="h-5 w-5 animate-spin text-teal-600" />
                <span>Loading partner batch requests...</span>
              </div>
            ) : error || !response?.success ? (
              <div className="px-6 py-16 text-center sm:px-8">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-rose-50 text-rose-600">
                  <AlertCircle className="h-6 w-6" />
                </div>
                <h3 className="mt-4 text-lg font-semibold text-slate-900">Unable to load partner batches</h3>
                <p className="mt-2 text-sm text-slate-500">
                  {error instanceof Error ? error.message : "Something went wrong while fetching batch requests."}
                </p>
              </div>
            ) : batches.length === 0 ? (
              <div className="px-6 py-16 text-center sm:px-8">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-teal-50 text-teal-600">
                  <ClipboardList className="h-6 w-6" />
                </div>
                <h3 className="mt-4 text-lg font-semibold text-slate-900">No batch requests found</h3>
                <p className="mt-2 text-sm text-slate-500">
                  Batches submitted by your canvassers will appear here for partner review.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200">
                  <thead className="bg-slate-50/90">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                        Batch
                      </th>
                      {/* <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                        Canvasser
                      </th> */}
                      <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                        Status
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                        Forms
                      </th>
                      {hasQualityScores && (<th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                        Quality
                      </th>)}
                      <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                        Submitted
                      </th>
                      <th className="px-6 py-4 text-center text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                        Details
                      </th>
                      {/* <th className="px-6 py-4 text-right text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                        Actions
                      </th> */}
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-200 bg-white">
                    {batches.map((batch) => {
                      const isExpanded = expandedBatchId === batch.id;
                      const isReviewing = reviewingBatchId === batch.id;
                      const isSubmitting = submittingBatchId === batch.id;
                      const isAcknowledging = acknowledgingBatchId === batch.id;
                      const hasSupervisorVerifiedBatch = Boolean(batch.verifiedAt);
                      const hasBeenDeliveredToSoe = Boolean(batch.deliveredAt);
                      const normalizedBatchStatus = batch.status?.toUpperCase();
                      const isRejectedBatch = normalizedBatchStatus === "REJECTED";
                      const isAwaitingAcknowledgementBatch =
                        normalizedBatchStatus === "AWAITING_ACKNOWLEDGEMENT" ||
                        normalizedBatchStatus === "AWAITING_ACKNOWLEDGMENT";
                      const hasBatchAcknowledgement =
                        !isSupervisor || Boolean(batch.acknowledgedAt) || acknowledgedBatchIds.has(batch.id);
                      const isCurrentReviewAction = isReviewing ? reviewAction : null;
                      const isUploadingReceipt = uploadingReceiptBatchId === batch.id;
                      const isDeliveringReceipt = deliveringBatchId === batch.id;
                      const canRejectBatch = batch.canReject === true;
                      const deliveryCountdown = getDeliveryCountdown(batch.verifiedAt, nowTimestamp);
                      const previousStepLabels = [
                        hasBatchAcknowledgement ? "Collection confirmed" : null,
                        isRejectedBatch ? "Rejected and closed" : hasSupervisorVerifiedBatch ? "Digital review submitted" : null,
                        !isRejectedBatch && hasBeenDeliveredToSoe ? "Physical handover confirmed" : null,
                      ].filter(Boolean);
                      return (
                        <Fragment key={batch.id}>
                          <tr
                            className="cursor-pointer transition hover:bg-slate-50/80"
                            onClick={() => toggleExpandedRow(batch.id)}
                          >
                            <td className="px-6 py-5 align-top">
                              <div className="space-y-1">
                                <p className="text-sm font-semibold text-slate-900">Batch #{batch.id}</p>
                                <p className="text-xs text-slate-500">
                                  submitted {formatDateValue(batch.createdAt)}
                                </p>
                              </div>
                            </td>

                     

                            <td className="px-6 py-5 align-top">
                              <div className="space-y-2">
                                <span
                                  className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${getBatchStatusClasses(
                                    batch.status
                                  )}`}
                                >
                                  {batch.status.replace(/_/g, " ")}
                                </span>
                                <p className="text-xs text-slate-500">
                                  {batch.overdueFlag ? "Overdue for verification" : "On schedule"}
                                </p>
                              </div>
                            </td>

                            <td className="px-6 py-5 align-top">
                              <div className="grid gap-2 text-sm text-slate-700">
                                <div className="flex items-center justify-between gap-5">
                                  <span>Claimed</span>
                                  <span className="font-semibold text-slate-900">{batch.claimedForms}</span>
                                </div>
                                <div className="flex items-center justify-between gap-5">
                                  <span>Verified</span>
                                  <span className="font-semibold text-emerald-700">{batch.verifiedForms}</span>
                                </div>
                                <div className="flex items-center justify-between gap-5">
                                  <span>Rejected</span>
                                  <span className="font-semibold text-rose-700">{batch.rejectedForms}</span>
                                </div>
                              </div>
                            </td>

                            {hasQualityScores && (<td className="px-6 py-5 align-top">
                              <div className="space-y-2 text-sm text-slate-700">
                                {batch.accuracyScore && (<p>
                                  <span className="text-slate-500">Accuracy:</span>{" "}
                                  <span className="font-medium text-slate-900">{formatScore(batch.accuracyScore)}</span>
                                </p>)}
                                {batch.finalQualityScore && (<p>
                                  <span className="text-slate-500">Final:</span>{" "}
                                  <span className="font-medium text-slate-900">{formatScore(batch.finalQualityScore)}</span>
                                </p>)}
                                {batch.verificationMode && (<p>
                                  <span className="text-slate-500">Mode:</span>{" "}
                                  <span className="font-medium text-slate-900">{batch.verificationMode}</span>
                                </p>)}
                              </div>
                            </td>)}

                            <td className="px-6 py-5 align-top">
                              <div className="space-y-1 text-sm text-slate-700">
                                <p>{formatDateValue(batch.submittedAt)}</p>
                                <p className="text-xs text-slate-500">
                                  Verified {formatDateValue(batch.verifiedAt)}
                                </p>
                              </div>
                            </td>

                            <td className="px-6 py-5 align-top text-left">
                              <div className="flex flex-col items-start gap-2">
                                {previousStepLabels.length > 0 && (
                                  <p className="max-w-full truncate text-[10px] text-slate-400">
                                    {previousStepLabels.join(" / ")}
                                  </p>
                                )}

                                <div className="flex justify-start gap-2">
                                {isRejectedBatch ? (
                                  <div className="inline-flex items-center gap-2 rounded-full border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-700">
                                    <AlertCircle className="h-4 w-4" />
                                    Fully rejected and closed
                                  </div>
                                ) : hasBeenDeliveredToSoe ? (
                                  <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700">
                                    <CheckCircle2 className="h-4 w-4" />
                                    Fully resolved
                                  </div>
                                ) : hasSupervisorVerifiedBatch ? (
                                  <Button
                                    type="button"
                                    variant="secondary"
                                    size="small"
                                    label="Confirm SOE handover"
                                    disabled={isDeliveringReceipt || isUploadingReceipt}
                                    additionalClassname="!w-auto min-w-[180px] !h-[32px] px-3 !text-xs !border-teal-200 !text-teal-700 hover:!bg-teal-50"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      setExpandedBatchId(batch.id);
                                    }}
                                  />
                                ) : isAwaitingAcknowledgementBatch ? (
                                  <Button
                                    type="button"
                                    variant="secondary"
                                    size="small"
                                    label={isAcknowledging ? "Acknowledging..." : "Awaiting acknowledgement"}
                                    loading={isAcknowledging}
                                    disabled={isAcknowledging}
                                    additionalClassname="!w-auto min-w-[190px] !h-[32px] px-3 !text-xs !border-blue-200 !text-blue-700 hover:!bg-blue-50"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleOpenAcknowledgementModal(batch);
                                    }}
                                  />
                                ) : hasBatchAcknowledgement ? (
                                  <>
                                    <button
                                      type="button"
                                      disabled={isSubmitting || hasSupervisorVerifiedBatch}
                                      className="inline-flex items-center gap-2 rounded-full border border-teal-200 bg-teal-50 px-3 py-1.5 text-xs font-semibold text-teal-700 transition hover:border-teal-300 hover:bg-teal-100 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        if (isReviewing && reviewAction === "VERIFY") {
                                          void handleSubmitBatchVerification(batch.id);
                                          return;
                                        }

                                        handleStartBatchReview(batch, "VERIFY");
                                      }}
                                    >
                                      {isSubmitting ? (
                                        <>
                                          <Loader2 className="h-4 w-4 animate-spin" />
                                          Submitting...
                                        </>
                                      ) : isReviewing && reviewAction === "VERIFY" ? (
                                        "Verify batch and submit to SOE"
                                      ) : (
                                        "Verify batch"
                                      )}
                                    </button>

                                    <button
                                      type="button"
                                      disabled={
                                        isSubmitting ||
                                        hasSupervisorVerifiedBatch ||
                                        !canRejectBatch
                                      }
                                      className="inline-flex items-center gap-2 rounded-full border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-700 transition hover:border-rose-300 hover:bg-rose-100 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400"
                                      title={
                                        canRejectBatch
                                          ? undefined
                                          : "Reject is only available when every form has invalid signature, completeness, and legibility checks."
                                      }
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        if (isReviewing && reviewAction === "REJECT") {
                                          void handleSubmitBatchVerification(batch.id);
                                          return;
                                        }

                                        handleStartBatchReview(batch, "REJECT");
                                      }}
                                    >
                                      {isReviewing && reviewAction === "REJECT" ? (
                                        "Reject and close batch"
                                      ) : (
                                        "Reject batch"
                                      )}
                                    </button>
                                  </>
                                ) : (
                                  <Button
                                    type="button"
                                    variant="secondary"
                                    size="small"
                                    label={isAcknowledging ? "Acknowledging..." : "Awaiting acknowledgement"}
                                    loading={isAcknowledging}
                                    disabled={isAcknowledging}
                                    additionalClassname="!w-auto min-w-[190px] !h-[32px] px-3 !text-xs !border-blue-200 !text-blue-700 hover:!bg-blue-50"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleOpenAcknowledgementModal(batch);
                                    }}
                                  />
                                )}

                                {!batch.submittedAt && !batch.verifiedAt && !batch.deliveredAt && (
                                  <button
                                    type="button"
                                    className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      toggleExpandedRow(batch.id);
                                    }}
                                  >
                                    {isExpanded ? "Hide forms" : "View forms"}
                                    {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                  </button>
                                )}
                                </div>
                              </div>
                            </td>
                          </tr>

                          {isExpanded && (
                            <tr className="bg-slate-50/70">
                              <td colSpan={7} className="px-6 py-6">
                                <div className="flex flex-col gap-4">
                                  {isRejectedBatch ? (
                                    <div className="rounded-2xl border border-rose-200 bg-rose-50 p-5">
                                      <div className="flex items-start gap-3">
                                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white text-rose-700">
                                          <AlertCircle className="h-5 w-5" />
                                        </div>
                                        <div>
                                          <p className="text-[10px] uppercase tracking-[0.2em] text-rose-700">
                                            Collection confirmed / Rejected and closed
                                          </p>
                                          <h3 className="mt-1 text-base font-semibold text-rose-950">
                                            Batch #{batch.id} is fully rejected and closed.
                                          </h3>
                                          <p className="mt-2 text-sm leading-6 text-rose-900">
                                            This batch was rejected after acknowledgement. No Supervisor of Elections delivery or receipt upload is required for this batch.
                                          </p>
                                          <div className="mt-4 grid gap-2 text-sm text-rose-900 sm:grid-cols-3">
                                            <span>Collected: {formatDateValue(batch.acknowledgedAt ?? null)}</span>
                                            {batch.submittedAt && <span>Submitted: {formatDateValue(batch.submittedAt)}</span>}
                                            {batch.verifiedAt && <span>Closed: {formatDateValue(batch.verifiedAt)}</span>}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ) : hasBeenDeliveredToSoe ? (
                                    <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
                                      <div className="flex items-start gap-3">
                                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white text-emerald-700">
                                          <CheckCircle2 className="h-5 w-5" />
                                        </div>
                                        <div>
                                          <p className="text-[10px] uppercase tracking-[0.2em] text-emerald-700">
                                            Collection confirmed / Digital review submitted / Physical handover confirmed
                                          </p>
                                          <h3 className="mt-1 text-base font-semibold text-emerald-950">
                                            Batch #{batch.id} is fully handled and closed.
                                          </h3>
                                          <p className="mt-2 text-sm leading-6 text-emerald-900">
                                            This batch has been reviewed, submitted to the Supervisor of Elections, delivered physically, and resolved.
                                          </p>
                                          <div className="mt-4 grid gap-2 text-sm text-emerald-900 sm:grid-cols-3">
                                            <span>Collected: {formatDateValue(batch.acknowledgedAt ?? null)}</span>
                                            {batch.submittedAt && (<span>Submitted: {formatDateValue(batch.submittedAt)}</span>)}
                                            <span>Delivered: {formatDateValue(batch.deliveredAt ?? null)}</span>
                                          </div>
                                          {batch.receiptUrl && (
                                            <a
                                              href={batch.receiptUrl}
                                              target="_blank"
                                              rel="noreferrer"
                                              className="mt-4 inline-flex text-sm font-semibold text-emerald-800 underline"
                                            >
                                              View Supervisor of Elections receipt
                                            </a>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  ) : hasSupervisorVerifiedBatch ? (
                                    <div className="rounded-2xl border border-teal-200 bg-white p-5">
                                      <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
                                        <div className="max-w-2xl">
                                          <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400">
                                            Collection confirmed / Digital review submitted
                                          </p>
                                          <h3 className="mt-1 text-base font-semibold text-slate-900">
                                            Forms and batch saved, reviewed, and submitted successfully.
                                          </h3>
                                          <p className="mt-2 text-sm leading-6 text-slate-600">
                                            Hand over all physical voter forms for batch #{batch.id} to the Supervisor of Elections office within 7 days, then upload the receipt issued by that office.
                                          </p>
                                          <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-800">
                                            <AlertCircle className="h-4 w-4" />
                                            {deliveryCountdown.isOverdue
                                              ? `You are ${deliveryCountdown.label} overdue to submit to SOE`
                                              : `${deliveryCountdown.label} left to complete physical handover`}
                                          </div>
                                        </div>

                                        <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-slate-50 p-4">
                                          <p className="text-sm font-semibold text-slate-900">
                                            Supervisor of Elections receipt
                                          </p>
                                          <p className="mt-1 text-xs leading-5 text-slate-500">
                                            Upload one PNG, JPG, JPEG, or PDF receipt from the Supervisor of Elections office.
                                          </p>

                                          <div className="mt-4 space-y-4">
                                            <label className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 bg-white px-4 py-5 text-sm font-medium text-slate-700 transition hover:border-teal-300 hover:text-teal-700">
                                              {isUploadingReceipt ? (
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                              ) : (
                                                <Upload className="h-4 w-4" />
                                              )}
                                              {isUploadingReceipt
                                                ? "Uploading receipt..."
                                                : receiptFileNames[batch.id] || "Upload receipt"}
                                              <input
                                                type="file"
                                                accept="image/png,image/jpg,image/jpeg,application/pdf"
                                                className="hidden"
                                                disabled={isUploadingReceipt || isDeliveringReceipt}
                                                onClick={(event) => event.stopPropagation()}
                                                onChange={(event) => {
                                                  void handleReceiptUpload(batch.id, event.target.files?.[0]);
                                                  event.currentTarget.value = "";
                                                }}
                                              />
                                            </label>

                                            <Textarea
                                              label="Delivery notes"
                                              name={`deliveryNotes-${batch.id}`}
                                              value={deliveryNotes[batch.id] ?? ""}
                                              onChange={(event) =>
                                                setDeliveryNotes((current) => ({
                                                  ...current,
                                                  [batch.id]: event.target.value,
                                                }))
                                              }
                                              debounce={0}
                                              rows={4}
                                              color="white"
                                              placeholder="Delivered to Supervisor of Elections office. Received by Officer A.B."
                                              helper="Optional."
                                              disabled={isDeliveringReceipt}
                                            />

                                            <Button
                                              type="button"
                                              variant="primary"
                                              size="medium"
                                              label={isDeliveringReceipt ? "Submitting..." : "Submit receipt"}
                                              loading={isDeliveringReceipt}
                                              disabled={!receiptUrls[batch.id] || isUploadingReceipt || isDeliveringReceipt}
                                              additionalClassname="!w-full !h-[40px]"
                                              onClick={(event) => {
                                                event.stopPropagation();
                                                void handleSubmitDeliveryReceipt(batch.id);
                                              }}
                                            />
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ) : (
                                    <BatchRecordsPanel
                                      batchId={batch.id}
                                      isVerificationMode={isReviewing}
                                      reviewAction={isCurrentReviewAction}
                                      selectedStatuses={batchFormStatuses[batch.id] ?? {}}
                                      pendingRecordId={verifyingRecordId}
                                      onStatusChange={(recordId, status) =>
                                        handleBatchRecordStatusChange(batch.id, recordId, status)
                                      }
                                      onSubmitVerification={() => void handleSubmitBatchVerification(batch.id)}
                                      isSubmittingVerification={isSubmitting}
                                    />
                                  )}

                                  <div className="rounded-2xl border border-slate-200 bg-white p-5">
                                    <h3 className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
                                      Batch summary
                                    </h3>

                                    <div className="mt-4 space-y-3 text-sm text-slate-700">
                                      <div className="flex items-center justify-between gap-4">
                                        <span>Incomplete forms</span>
                                        <span className="font-semibold text-slate-900">{batch.incompleteForms}</span>
                                      </div>
                                      <div className="flex items-center justify-between gap-4">
                                        <span>Hours to verify</span>
                                        <span className="font-semibold text-slate-900">{batch.hoursToVerify ?? "--"}</span>
                                      </div>
                                      <div className="flex items-center justify-between gap-4">
                                        <span>Rejection rate</span>
                                        <span className="font-semibold text-slate-900">{formatScore(batch.rejectionRate)}</span>
                                      </div>
                                      <div className="flex items-center justify-between gap-4">
                                        <span>Timeliness</span>
                                        <span className="font-semibold text-slate-900">{formatScore(batch.timelinessScore)}</span>
                                      </div>
                                      
                                    </div>

                                    <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                                        Notes
                                      </p>
                                      <p className="mt-2 whitespace-pre-line text-sm leading-6 text-slate-700">
                                        {batch.notes?.trim() || "No notes were attached to this batch."}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      </div>

      {pendingBatchSubmission && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          aria-modal="true"
          role="dialog"
          onClick={() => {
            if (!verifyBatchMutation.isPending) {
              setPendingBatchSubmission(null);
            }
          }}
        >
          <div className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm" />
          <div
            className="relative w-full max-w-2xl rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-teal-50 text-teal-700">
                <FileSearch className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-teal-700">
                  {pendingBatchSubmission.action === "REJECT" ? "Close batch" : "Submit to SOE"}
                </p>
                <h3 className="mt-1 text-xl font-semibold text-slate-900">
                  {pendingBatchSubmission.action === "REJECT" ? "Reject and close" : "Submit"} batch #{pendingBatchSubmission.batchId}
                </h3>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  {pendingBatchSubmission.action === "REJECT"
                    ? "You are about to reject this batch and close it. It will not continue to Supervisor of Elections delivery."
                    : "You are about to submit all forms in this batch and send the batch to the Supervisor of Elections (SOE)."}
                  {" "}Review the supervisor counts before proceeding.
                </p>
              </div>
            </div>

            <div className="mt-6 grid gap-3 sm:grid-cols-4">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Total forms</p>
                <p className="mt-2 text-2xl font-semibold text-slate-900">{pendingBatchSubmission.totalForms}</p>
              </div>
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-emerald-700">Verified</p>
                <p className="mt-2 text-2xl font-semibold text-emerald-800">{pendingBatchSubmission.verifiedForms}</p>
              </div>
              <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-rose-700">Rejected</p>
                <p className="mt-2 text-2xl font-semibold text-rose-800">{pendingBatchSubmission.rejectedForms}</p>
              </div>
              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-amber-700">Incomplete</p>
                <p className="mt-2 text-2xl font-semibold text-amber-800">{pendingBatchSubmission.incompleteForms}</p>
              </div>
            </div>

            {pendingBatchSubmission.incompleteForms > 0 && (
              <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-800">
                Some forms in this batch have not been explicitly verified or rejected by the supervisor. They will be counted as incomplete or invalid in this submission.
              </div>
            )}

            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <Button
                type="button"
                variant="tertiary"
                size="medium"
                label="Cancel"
                disabled={verifyBatchMutation.isPending}
                additionalClassname="!w-full sm:!w-auto px-4"
                onClick={() => setPendingBatchSubmission(null)}
              />
              <Button
                type="button"
                variant="primary"
                size="medium"
                label={
                  verifyBatchMutation.isPending
                    ? pendingBatchSubmission.action === "REJECT"
                      ? "Closing..."
                      : "Submitting..."
                    : pendingBatchSubmission.action === "REJECT"
                      ? "Reject and close batch"
                      : "Proceed"
                }
                loading={verifyBatchMutation.isPending}
                disabled={verifyBatchMutation.isPending}
                additionalClassname="!w-full sm:!w-auto px-4"
                onClick={() => void handleProceedBatchSubmissionToSoe()}
              />
            </div>
          </div>
        </div>
      )}

      {acknowledgementModalBatch && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          aria-modal="true"
          role="dialog"
          onClick={() => {
            if (!acknowledgeBatchMutation.isPending) {
              setAcknowledgementModalBatch(null);
              setAcknowledgementNotes("");
            }
          }}
        >
          <div className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm" />
          <div
            className="relative w-full max-w-xl rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-blue-50 text-blue-700">
                <ClipboardList className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-blue-700">
                  Batch collection
                </p>
                <h3 className="mt-1 text-xl font-semibold text-slate-900">
                  Confirm batch #{acknowledgementModalBatch.id}
                </h3>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  Acknowledge that the paper voter forms for this batch have been handed over and received.
                </p>
              </div>
            </div>

            <div className="mt-5">
              <Textarea
                label="Additional notes"
                name="acknowledgementNotes"
                value={acknowledgementNotes}
                onChange={(event) => setAcknowledgementNotes(event.target.value)}
                debounce={0}
                rows={5}
                color="white"
                placeholder="Add optional notes for this batch acknowledgement"
                helper="Optional. These notes will be sent with the acknowledgement."
              />
            </div>

            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <Button
                type="button"
                variant="tertiary"
                size="medium"
                label="Cancel"
                disabled={acknowledgeBatchMutation.isPending}
                additionalClassname="!w-full sm:!w-auto px-4"
                onClick={() => {
                  setAcknowledgementModalBatch(null);
                  setAcknowledgementNotes("");
                }}
              />
              <Button
                type="button"
                variant="primary"
                size="medium"
                label={acknowledgeBatchMutation.isPending ? "Acknowledging..." : "I acknowledge"}
                loading={acknowledgeBatchMutation.isPending}
                disabled={acknowledgeBatchMutation.isPending}
                additionalClassname="!w-full sm:!w-auto px-4"
                onClick={() => void handleAcknowledgeBatchCollection()}
              />
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default PartnerBatchRequests;
